import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, FlatList, RefreshControl, TouchableOpacity } from 'react-native';
import { Text, Card, Chip, ActivityIndicator } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';

const SolicitudesScreen = ({ navigation }) => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('pendiente');

  const loadSolicitudes = async () => {
    try {
      const data = await api.superAdmin.getSolicitudes(filtroEstado);
      setSolicitudes(Array.isArray(data) ? data : data.solicitudes || []);
    } catch (error) {
      console.error('Error cargando solicitudes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadSolicitudes();
  }, [filtroEstado]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      loadSolicitudes();
    });
    return unsubscribe;
  }, [navigation, filtroEstado]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadSolicitudes();
    setRefreshing(false);
  }, [filtroEstado]);

  const getEstadoConfig = (estado) => {
    const configs = {
      pendiente: { color: '#FF9800', icon: 'time-outline' },
      aprobada: { color: '#4CAF50', icon: 'checkmark-circle-outline' },
      rechazada: { color: '#f44336', icon: 'close-circle-outline' },
    };
    return configs[estado] || configs.pendiente;
  };

  const renderSolicitud = ({ item }) => {
    const config = getEstadoConfig(item.estado);
    return (
      <TouchableOpacity onPress={() => navigation.navigate('SolicitudDetalle', { solicitud: item })}>
        <Card style={styles.card}>
          <Card.Content>
            <View style={styles.cardHeader}>
              <View style={{ flex: 1 }}>
                <Text style={styles.empresaName}>{item.nombre_empresa}</Text>
                <Text style={styles.empresaSlug}>{item.slug}</Text>
              </View>
              <Chip
                style={{ backgroundColor: config.color + '20' }}
                textStyle={{ fontSize: 11, color: config.color }}
              >
                {item.estado}
              </Chip>
            </View>

            <View style={styles.infoContainer}>
              <View style={styles.infoItem}>
                <Ionicons name="person-outline" size={14} color="#888" />
                <Text style={styles.infoText}>{item.nombre_contacto}</Text>
              </View>
              <View style={styles.infoItem}>
                <Ionicons name="mail-outline" size={14} color="#888" />
                <Text style={styles.infoText}>{item.email}</Text>
              </View>
              <View style={styles.infoItem}>
                <Ionicons name="calendar-outline" size={14} color="#888" />
                <Text style={styles.infoText}>
                  {item.created_at ? new Date(item.created_at).toLocaleDateString('es-HN') : '-'}
                </Text>
              </View>
            </View>

            {item.estado === 'pendiente' && (
              <View style={styles.actionHint}>
                <Ionicons name="arrow-forward" size={16} color="#007bff" />
                <Text style={styles.actionHintText}>Toca para revisar y aprobar</Text>
              </View>
            )}
          </Card.Content>
        </Card>
      </TouchableOpacity>
    );
  };

  if (loading && solicitudes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.filtrosRow}>
        {['pendiente', 'aprobada', 'rechazada'].map((estado) => (
          <Chip
            key={estado}
            selected={filtroEstado === estado}
            onPress={() => setFiltroEstado(estado)}
            style={[styles.filtroChip, filtroEstado === estado && styles.filtroChipActive]}
            textStyle={filtroEstado === estado ? { color: '#fff' } : {}}
          >
            {estado.charAt(0).toUpperCase() + estado.slice(1)}
          </Chip>
        ))}
      </View>

      <FlatList
        data={solicitudes}
        renderItem={renderSolicitud}
        keyExtractor={(item) => String(item.id)}
        contentContainerStyle={styles.listContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="document-text-outline" size={48} color="#ccc" />
            <Text style={styles.emptyText}>No hay solicitudes {filtroEstado}s</Text>
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  filtrosRow: { flexDirection: 'row', padding: 12, gap: 8 },
  filtroChip: { backgroundColor: 'white' },
  filtroChipActive: { backgroundColor: '#007bff' },
  listContent: { padding: 12, paddingTop: 4 },
  card: { marginBottom: 10, backgroundColor: 'white' },
  cardHeader: { flexDirection: 'row', alignItems: 'center' },
  empresaName: { fontSize: 16, fontWeight: '600', color: '#333' },
  empresaSlug: { fontSize: 12, color: '#007bff', marginTop: 2 },
  infoContainer: { marginTop: 10 },
  infoItem: { flexDirection: 'row', alignItems: 'center', marginBottom: 4 },
  infoText: { fontSize: 13, color: '#666', marginLeft: 6 },
  actionHint: { flexDirection: 'row', alignItems: 'center', marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#f0f0f0' },
  actionHintText: { fontSize: 13, color: '#007bff', marginLeft: 6 },
  emptyContainer: { alignItems: 'center', paddingVertical: 40 },
  emptyText: { fontSize: 15, color: '#999', marginTop: 12 },
});

export default SolicitudesScreen;
